/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect } from "react";
import "./Subscription.css";
import { PLAN_PERIODS, TIER_TAGLINES, formatRand, monthlyPriceCents, onceOffNote, planFeatures, usePlans } from "./utils/marksurePlans";

const FALLBACK_PUBLIC_API_BASE = "https://marksure.tech/api";

const isLocalHostName = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "localhost" || normalized === "127.0.0.1";
};

const getPublicApiBase = () => {
  const configuredBase = String(process.env.REACT_APP_API_URL || "").trim();

  if (/^https?:\/\//i.test(configuredBase)) {
    try {
      const configuredUrl = new URL(configuredBase);
      if (isLocalHostName(configuredUrl.hostname)) {
        return FALLBACK_PUBLIC_API_BASE;
      }
    } catch (error) {
      return FALLBACK_PUBLIC_API_BASE;
    }

    return configuredBase.replace(/\/$/, "");
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return FALLBACK_PUBLIC_API_BASE;
    }

    if (configuredBase.startsWith("/")) {
      return `${window.location.origin}${configuredBase}`.replace(/\/$/, "");
    }
  }

  return configuredBase
    ? configuredBase.replace(/\/$/, "")
    : FALLBACK_PUBLIC_API_BASE;
};

const parseApiResponse = async (response) => {
  const rawText = await response.text();

  try {
    return JSON.parse(rawText);
  } catch (error) {
    const compactText = rawText.replace(/\s+/g, " ").trim();
    throw new Error(
      compactText
        ? `Unexpected server response: ${compactText.slice(0, 180)}`
        : "Empty server response",
    );
  }
};

// SVG Checkmark Icon
const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#1f4a3a", marginRight: "10px" }}
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

// Legal Modal Component
const LegalModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Terms of Service & Legal Disclaimer</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <p className="terms-updated">Last updated: 28 October 2026</p>
          <h3>Terms of Service</h3>
          <p>By subscribing to Marksure, you agree to the following terms:</p>
          <ul>
            <li>
              This is a private educational tool provided by Marksure (Pty) Ltd.
            </li>
            <li>
              The service is not affiliated with or endorsed by the Gauteng
              Department of Education (GDE) or any other government entity.
            </li>
            <li>All data entered remains your property and responsibility.</li>
            <li>
              You are responsible for ensuring compliance with all applicable
              laws and regulations.
            </li>
            <li>
              Service availability and features may change without notice.
            </li>
            <li>
              Each account is for one teacher. Accounts and login details may
              not be shared.
            </li>
          </ul>

          <h3>Plans and AI-marked scripts</h3>
          <ul>
            <li>
              Each plan includes a set number of AI-marked scripts for its
              period (month, term or year), shown on the plan when you buy it.
              One script is one learner's answer paper, of any length.
            </li>
            <li>
              A script is only counted once it has been marked successfully.
              Scripts that fail to process or contain no answers are not
              counted, and re-marking the same script within 24 hours is free
              (up to two times).
            </li>
            <li>
              Unused scripts from your plan expire at the end of that plan's
              period and do not carry over. Top-up packs of extra scripts do not
              expire while your account remains active.
            </li>
            <li>
              When your scripts run out, AI marking pauses until you buy a
              top-up, choose a bigger plan or your next period starts. The rest
              of Marksure keeps working.
            </li>
            <li>
              Prices and script allowances may change for future purchases. A
              change never affects a plan or top-up you have already paid for.
            </li>
          </ul>

          <h3>AI marking</h3>
          <ul>
            <li>
              AI marks are suggestions to save you time. They can contain
              mistakes, especially with unclear handwriting or poor-quality
              scans.
            </li>
            <li>
              You remain responsible for every final mark. Review the AI's
              suggestions and correct them before saving or reporting marks.
            </li>
            <li>
              Only upload scripts and memoranda you are authorised to use for
              assessment at your school.
            </li>
          </ul>

          <h3>How scripts are processed</h3>
          <p>
            To mark a script, its pages and the memorandum are sent securely to
            our AI provider (Google's Gemini service), which may process them
            on servers outside South Africa. They are used only to produce the
            marking for your request. Temporary uploads are deleted from our
            servers after processing, and saved results stay in your
            authenticated Marksure account. See our Privacy Policy for details.
          </p>

          <h3>Legal Disclaimer</h3>
          <p>
            <strong>Indemnity:</strong> You hereby indemnify and hold harmless
            Marksure (Pty) Ltd, its directors, employees, and agents from any
            claims, damages, losses, or liabilities arising from your use of
            this service.
          </p>
          <p>
            <strong>Data Responsibility:</strong> You acknowledge that this tool
            is not an official system of the Department of Basic Education or
            SASAMS. Users are responsible for verifying all data against
            official records.
          </p>
          <p>
            <strong>No Warranty:</strong> This service is provided "as is"
            without warranty of any kind, express or implied.
          </p>

          <h3>Payment Terms</h3>
          <p>
            All payments are final and non-refundable. Each plan is a once-off
            payment that gives access for the period you select (1 month, 3
            months or 12 months). Plans do not renew automatically and no
            recurring debit is taken; you choose whether to pay again when your
            plan ends. Card payments are processed securely by Yoco; Marksure
            never sees or stores your card details.
          </p>

          <h3>Changes to these terms</h3>
          <p>
            We may update these terms from time to time. The date at the top
            shows when they last changed. Continuing to use Marksure after a
            change means you accept the updated terms.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Subscription = () => {
  const publicApiBase = getPublicApiBase();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    email: "",
    password: "",
    schoolName: "",
    plan: "",
  });
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("month");
  // Prices come from the app's own API (local while developing); checkout stays on publicApiBase.
  const plansApiBase = String(process.env.REACT_APP_API_URL || publicApiBase).replace(/\/$/, "");
  const { plans: availablePlans, topup, error: plansError } = usePlans(plansApiBase);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");
    const plan = urlParams.get("plan");

    if (status === "success") {
      setShowRegistration(false);
      setMessage(
        "Payment received. Your account is being activated now. If you cannot log in within a minute, please contact support.",
      );
    } else if (status === "cancelled") {
      setMessage("Payment was cancelled.");
    } else if (status === "failed") {
      setMessage("Payment failed. Please try again.");
    }

    if (status) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSubscribe = (plan) => {
    if (!termsAccepted) {
      alert("Please accept the Terms of Service before subscribing.");
      return;
    }
    setRegistrationData((prev) => ({ ...prev, plan: plan.id }));
    setShowRegistration(true);
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    setRegistrationLoading(true);

    try {
      const response = await fetch(
        `${publicApiBase}/create_public_subscription.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(registrationData),
        },
      );

      const result = await parseApiResponse(response);

      if (response.ok && result.success) {
        window.location.href = result.redirectUrl;
      } else {
        alert("Failed to start checkout: " + (result.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    // Validation logic
    if (
      (name === "email" || name === "schoolName") &&
      /\d{13,19}/.test(value.replace(/\s/g, ""))
    ) {
      alert(
        `Please enter a valid ${name === "email" ? "email" : "school name"}, not a credit card number.`,
      );
      return;
    }
    setRegistrationData({ ...registrationData, [name]: value });
  };

  // Best saving per month versus paying monthly for the same plan, e.g. "save 20%".
  const savingFor = (periodId) => {
    const savings = availablePlans
      .filter((plan) => plan.period === periodId)
      .map((plan) => {
        const monthly = availablePlans.find((other) => other.tier === plan.tier && other.period === "month");
        return monthly ? 1 - monthlyPriceCents(plan) / monthly.price_cents : 0;
      });
    return Math.floor(Math.max(0, ...savings) * 100);
  };

  const plans = availablePlans
    .filter((plan) => plan.period === billingPeriod)
    .map((plan) => ({
      id: plan.id,
      name: plan.label,
      tagline: TIER_TAGLINES[plan.tier] || "",
      price: Math.round(monthlyPriceCents(plan) / 100).toLocaleString("en-ZA"),
      onceOff: onceOffNote(plan),
      features: planFeatures(plan),
      highlight: plan.tier === "teacher",
    }));

  // --- RENDER REGISTRATION ---
  if (showRegistration) {
    return (
      <div className="sub-wrapper">
        <div className="sub-card registration-card">
          <div className="header-section">
            <h1>Create your account</h1>
            <p>
              Your account will activate automatically as soon as Yoco confirms
              payment.
            </p>
          </div>

          <form onSubmit={handleRegistration} className="modern-form">
            <div className="input-group">
              <label>School Name</label>
              <input
                type="text"
                name="schoolName"
                value={registrationData.schoolName}
                onChange={handleRegistrationChange}
                required
                placeholder="e.g. Pretoria High"
              />
            </div>

            <div className="input-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                value={registrationData.email}
                onChange={handleRegistrationChange}
                required
                placeholder="name@school.co.za"
              />
            </div>

            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={registrationData.password}
                onChange={handleRegistrationChange}
                required
                placeholder="Min 6 characters"
                minLength="6"
              />
            </div>

            <button
              type="submit"
              disabled={registrationLoading}
              className="btn-primary full-width"
            >
              {registrationLoading
                ? "Opening checkout..."
                : "Continue to secure payment"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- RENDER PRICING PLANS ---
  return (
    <div className="sub-wrapper">
      <div className="sub-container">
        <div className="header-section center">
          <h1>Choose Your Plan</h1>
          <p>Simple, transparent pricing for South African educators.</p>
        </div>

        {message && (
          <div
            className={`alert-banner ${message.includes("successful") ? "success" : "error"}`}
          >
            {message}
          </div>
        )}

        {/* Terms of Service Section */}
        <div className="terms-section">
          <div className="terms-checkbox">
            <input
              type="checkbox"
              id="terms-accept"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <label htmlFor="terms-accept">
              I agree to the{" "}
              <button
                type="button"
                className="link-button"
                onClick={() => setShowLegalModal(true)}
              >
                Terms of Service
              </button>{" "}
              and acknowledge that this is a private tool, not affiliated with
              the GDE.
            </label>
          </div>
        </div>

        <div className="period-toggle" role="tablist" aria-label="Billing period">
          {PLAN_PERIODS.map((period) => (
            <button
              key={period.id}
              type="button"
              role="tab"
              aria-selected={billingPeriod === period.id}
              className={billingPeriod === period.id ? "active" : ""}
              onClick={() => setBillingPeriod(period.id)}
            >
              {period.label}
              {savingFor(period.id) >= 5 && <span className="period-saving">save up to {savingFor(period.id)}%</span>}
            </button>
          ))}
        </div>
        <div className="once-off-banner">
          <strong>Pay once, no contracts.</strong> Every plan is a once-off
          payment for the period you choose. Nothing is debited automatically
          and your plan does not renew by itself. When it ends, you decide
          whether to renew.
        </div>
        <p className="plan-allowance-note">
          Plans with AI marking include a set number of scripts each period
          (one script = one learner's paper, however many pages).
        </p>
        {plansError && <div className="alert-banner error">{plansError}</div>}
        {!plansError && plans.length === 0 && <p className="plan-allowance-note">Loading plans…</p>}

        <div className="plans-grid">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`plan-card ${plan.highlight ? "highlight" : ""}`}
            >
              {plan.highlight && (
                <div className="popular-badge">Most Popular</div>
              )}
              <div className="plan-header">
                <h2>{plan.name}</h2>
                {plan.tagline && <p className="plan-tagline">{plan.tagline}</p>}
                <div className="price-wrapper">
                  <span className="currency">R</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">/month</span>
                </div>
                <p className="once-off-note">{plan.onceOff}</p>
              </div>

              <ul className="feature-list">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <CheckIcon /> {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan)}
                disabled={loading || !termsAccepted}
                className={`btn-primary full-width ${plan.highlight ? "" : "outline"} ${!termsAccepted ? "disabled" : ""}`}
              >
                {loading ? "Processing..." : "Subscribe Now"}
              </button>
            </div>
          ))}
        </div>

        {topup && (
          <div className="topup-card">
            <div>
              <h3>Need more scripts?</h3>
              <p>
                Buy extra scripts any time from the marking page. Extra scripts
                are a once-off payment and never expire.
              </p>
            </div>
            <div className="topup-price">
              <strong>{formatRand(topup.price_cents)}</strong>
              <span>for {topup.scripts} scripts</span>
              <span>({formatRand(Math.round(topup.price_cents / topup.scripts))} per script)</span>
            </div>
          </div>
        )}

        <div className="bottom-link">
          <p>
            Already have an account? <a href="/">Log in here</a>
          </p>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
      />
    </div>
  );
};

export default Subscription;
